import * as React from "react";

// T-262 (V-B4) : champ date unique, toujours en JJ/MM/AAAA, quel que soit le navigateur
// (le champ natif suit la langue du navigateur : mm/dd/yyyy en anglais).
// Contrat identique au champ natif : `value` en ISO AAAA-MM-JJ, `onChange` reçoit un
// évènement dont `target.value` est l'ISO (ou "" si le champ est vidé).

export function isoVersAffichage(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

export function affichageVersIso(texte) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(texte || "").trim());
  if (!m) return null;
  const [, j, mo, a] = m;
  const d = new Date(Number(a), Number(mo) - 1, Number(j));
  if (d.getFullYear() !== Number(a) || d.getMonth() !== Number(mo) - 1 || d.getDate() !== Number(j)) return null;
  return `${a}-${mo}-${j}`;
}

// Bornes inclusives en ISO AAAA-MM-JJ (comme les attributs min/max du champ natif) ; borne absente ou illisible = pas de borne.
export function dansLesBornes(iso, min, max) {
  const borne = (b) => (/^\d{4}-\d{2}-\d{2}/.test(String(b || "")) ? String(b).slice(0, 10) : null);
  const bas = borne(min);
  const haut = borne(max);
  if (bas && iso < bas) return false;
  if (haut && iso > haut) return false;
  return true;
}

// Ajoute les barres au fil de la frappe : 12032026 -> 12/03/2026.
export function masquerSaisie(brut) {
  const c = String(brut || "").replace(/\D/g, "").slice(0, 8);
  if (c.length <= 2) return c;
  if (c.length <= 4) return `${c.slice(0, 2)}/${c.slice(2)}`;
  return `${c.slice(0, 2)}/${c.slice(2, 4)}/${c.slice(4)}`;
}

const ChampDate = React.forwardRef(function ChampDate(
  { value, onChange, onBlur, type, min, max, placeholder = "JJ/MM/AAAA", ...rest }, ref
) {
  const [texte, setTexte] = React.useState(isoVersAffichage(value));

  React.useEffect(() => {
    // Changement venu de l'extérieur (chargement, remise à zéro) : on réaffiche.
    setTexte((courant) => (affichageVersIso(courant) === (value || null) || (!value && !courant)
      ? courant : isoVersAffichage(value)));
  }, [value]);

  const emettre = (v) => onChange && onChange({ target: { value: v }, currentTarget: { value: v } });

  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={10}
      lang="fr"
      placeholder={placeholder}
      value={texte}
      onChange={(e) => {
        const t = masquerSaisie(e.target.value);
        setTexte(t);
        if (t === "") emettre("");
        else {
          const iso = affichageVersIso(t);
          // Hors min/max : la date n'est jamais émise et le champ revient à la dernière date retenue.
          if (iso && !dansLesBornes(iso, min, max)) setTexte(isoVersAffichage(value));
          else if (iso) emettre(iso);
        }
      }}
      onBlur={(e) => {
        // Saisie incomplète ou impossible : on revient à la dernière date valide.
        const lue = affichageVersIso(texte);
        const valide = lue && dansLesBornes(lue, min, max) ? lue : null;
        if (valide === null && texte !== "") setTexte(isoVersAffichage(value));
        // Contrat : à la sortie du champ aussi, target.value est l'ISO (ou ""), jamais le JJ/MM/AAAA affiché.
        if (onBlur) {
          const iso = texte === "" ? "" : (valide ?? affichageVersIso(isoVersAffichage(value)) ?? "");
          onBlur({ ...e, target: { ...e.target, value: iso }, currentTarget: { ...e.currentTarget, value: iso } });
        }
      }}
      {...rest}
    />
  );
});

export default ChampDate;
export { ChampDate };
